import { useState, useRef } from 'react';
import { type QueryLazyOptions, useLazyQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { handleApolloError } from '@atlassian/jira-issue-create-commons/src/common/utils/handle-apollo-error/index.tsx';
import type {
	QueryVariables,
	CreateConfigQueryResponse,
	FetchConfigData,
	CreateConfigQueryResponseWithML,
} from '../../common/types';
import { transformQueryData, transformQueryResults } from '../../common/utils/fetch-config-query';
import { useSmartFieldsMachineLearning } from '../../common/utils/use-smart-fields-ml';
import { getCreateConfigUserPreferredQuery } from './gql';
import { CREATE_CONFIG_USER_PREFERRED_QUERY } from './gql-old';
import type { UseFetchCreateConfigQuery } from './types';

const useFetchCreateConfigQuery: UseFetchCreateConfigQuery = (props = {}) => {
	const { displayOnlyRequiredFields = false, triggerPointKey } = props;
	const [loading, setLoading] = useState(false);
	const [fetchError, setFetchError] = useState<ApolloError | null>(null);
	const apiStartTime = useRef<number>(0);
	const [apiLatency, setApiLatency] = useState<number>(0);
	const [data, setData] = useState<FetchConfigData>({
		fieldTabs: [],
		fieldConfig: {},
		contextFields: {},
		meta: {
			areRequiredFieldsSupported: true,
			queryErrorMessage: '',
		},
		additionalServices: {},
	});

	const { withMachineLearning } = ff('gic-smart-fields-jira-labs_8uwvr')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useSmartFieldsMachineLearning()
		: { withMachineLearning: false };

	const [fetchConfig] = useLazyQuery<
		CreateConfigQueryResponse | CreateConfigQueryResponseWithML,
		QueryVariables
	>(
		ff('gic-smart-fields-jira-labs_8uwvr')
			? getCreateConfigUserPreferredQuery(withMachineLearning)
			: CREATE_CONFIG_USER_PREFERRED_QUERY,
		{
			fetchPolicy: 'no-cache',
			onCompleted: (queryData) => {
				setApiLatency(Math.floor(performance.now() - apiStartTime.current));
				if (ff('gic-smart-fields-jira-labs_8uwvr')) {
					setData(transformQueryResults(transformQueryData(queryData), displayOnlyRequiredFields));
				} else {
					setData(
						transformQueryResults(
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							queryData as CreateConfigQueryResponse,
							displayOnlyRequiredFields,
						),
					);
				}

				setLoading(false);
			},
			onError: (error: ApolloError) => {
				setFetchError(error);
				fireErrorAnalytics({
					meta: {
						id: 'useFetchCreateConfigQuery',
						packageName: 'jiraIssueCreate',
						teamName: 'gryffindor',
					},
					error,
					attributes: {
						errorType: handleApolloError(error),
						triggerPointKey,
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		},
	);
	const handleFetch = useRef((queryVariables?: QueryLazyOptions<QueryVariables>) => {
		setLoading(true);
		apiStartTime.current = performance.now();
		fetchConfig(queryVariables);
	});
	return [
		{
			isLoading: loading,
			fieldTabs: data.fieldTabs,
			fieldConfig: data.fieldConfig,
			contextFields: data.contextFields,
			meta: data.meta,
			error: fetchError,
			apiLatency,
			additionalServices: data.additionalServices,
		},
		handleFetch.current,
	];
};
export default useFetchCreateConfigQuery;
